"use client";

import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, ChevronLeft, Check } from "lucide-react";
import Link from "next/link";

// Each step shows: what the cashier taps, at what time (in seconds), the label
type Step = {
  t: number; // seconds from start
  kind: "cat" | "item" | "mod" | "add" | "pay" | "method" | "done";
  label: { en: string; es: string };
};

// Total speed-run target: 1.4s
const STEPS: Step[] = [
  { t: 0.0, kind: "cat", label: { en: "Tacos", es: "Tacos" } },
  { t: 0.3, kind: "item", label: { en: "Birria", es: "Birria" } },
  { t: 0.55, kind: "mod", label: { en: "Extra cheese", es: "Extra queso" } },
  { t: 0.8, kind: "add", label: { en: "Add", es: "Agregar" } },
  { t: 1.0, kind: "pay", label: { en: "Pay", es: "Cobrar" } },
  { t: 1.2, kind: "method", label: { en: "Cash", es: "Efectivo" } },
  { t: 1.4, kind: "done", label: { en: "Done", es: "Listo" } },
];

const LOOP_DURATION = 3.4; // 1.4s run + 2.0s rest
const TICK_MS = 16;

function useTimer() {
  const [tSec, setTSec] = useState(0);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (startedAt.current === null) startedAt.current = performance.now();
      const elapsed = (performance.now() - startedAt.current) / 1000;
      const loop = elapsed % LOOP_DURATION;
      setTSec(loop > 1.4 ? 1.4 : loop);
      if (loop > LOOP_DURATION - TICK_MS / 1000) {
        // ensures clean reset
      }
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return tSec;
}

export default function RushHourHero() {
  const { t } = useLang();
  const tSec = useTimer();

  let activeStep = STEPS[0];
  for (let i = STEPS.length - 1; i >= 0; i--) {
    if (tSec >= STEPS[i].t) {
      activeStep = STEPS[i];
      break;
    }
  }
  const isDone = activeStep.kind === "done";

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Built for the rush · 12:30 · the line", "Hecho para la hora pico · 12:30 · la fila")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("12:30.", "12:30.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Bring it.", "Que vengan.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[640px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "Two taps from menu to fired. Multi-cart for parallel ringing. Held orders for indecisive guests. Your POS shouldn't be a bottleneck — it should keep up with you.",
            "Dos taps del menú a la cocina. Multi-cart para cobrar y tomar orden a la vez. Órdenes en espera para los que no deciden. El POS no debería ser el cuello de botella — debería ir a tu velocidad."
          )}
        </motion.p>

        {/* iPad mockup with speed-run */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[1000px] mx-auto"
        >
          <div
            className="absolute -inset-4 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(244,114,43,0.12) 0%, transparent 65%)" }}
          />

          <div className="relative bg-[#1a1a1a] rounded-[28px] p-3 md:p-4 shadow-[0_40px_80px_rgba(0,0,0,0.18)]">
            {/* iPad bezel */}
            <div className="bg-white rounded-[20px] overflow-hidden">
              {/* POS top bar */}
              <div className="flex items-center justify-between px-5 py-3 bg-[#FAFAF8] border-b border-black/5">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-lg bg-redy-charcoal flex items-center justify-center text-white text-[10px] font-black">
                    R
                  </div>
                  <div className="text-[12px] font-bold text-redy-charcoal">
                    {t("Pepitos WAAO · Front Counter", "Pepitos WAAO · Mostrador")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#2ECC71" }} />
                  <div className="text-[11px] font-bold text-redy-secondary">12:34 PM</div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-0 min-h-[360px] md:min-h-[420px]">
                {/* Categories rail */}
                <div className="col-span-2 bg-[#FAFAF8] border-r border-black/5 p-2 space-y-1">
                  {[
                    { key: "Tacos", label: { en: "Tacos", es: "Tacos" } },
                    { key: "Bowls", label: { en: "Bowls", es: "Bowls" } },
                    { key: "Sides", label: { en: "Sides", es: "Sides" } },
                    { key: "Drinks", label: { en: "Drinks", es: "Bebidas" } },
                  ].map((c) => {
                    const isActive = activeStep.kind !== "cat" || c.key === "Tacos";
                    const isHighlighted =
                      c.key === "Tacos" && tSec >= 0 && tSec < 0.3;
                    return (
                      <div
                        key={c.key}
                        className="px-2 py-2 rounded-lg text-[11px] font-bold transition-all"
                        style={{
                          background: isHighlighted
                            ? "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)"
                            : c.key === "Tacos" && tSec >= 0.3
                              ? "rgba(244,114,43,0.08)"
                              : "transparent",
                          color: isHighlighted
                            ? "white"
                            : c.key === "Tacos" && tSec >= 0.3
                              ? "#D7263D"
                              : "#6B6762",
                          transform: isHighlighted ? "scale(1.04)" : "scale(1)",
                        }}
                      >
                        {t(c.label.en, c.label.es)}
                      </div>
                    );
                  })}
                </div>

                {/* Item grid */}
                <div className="col-span-7 p-3 grid grid-cols-3 gap-2 content-start">
                  {[
                    { key: "Birria", label: { en: "Birria", es: "Birria" }, price: "$9", emoji: "🌮" },
                    { key: "Pastor", label: { en: "Pastor", es: "Pastor" }, price: "$8", emoji: "🌮" },
                    { key: "Carnitas", label: { en: "Carnitas", es: "Carnitas" }, price: "$8", emoji: "🌮" },
                    { key: "Pollo", label: { en: "Pollo", es: "Pollo" }, price: "$7", emoji: "🌮" },
                    { key: "Asada", label: { en: "Asada", es: "Asada" }, price: "$9", emoji: "🌮" },
                    { key: "Lengua", label: { en: "Lengua", es: "Lengua" }, price: "$9", emoji: "🌮" },
                  ].map((it) => {
                    const isHighlighted = it.key === "Birria" && tSec >= 0.3 && tSec < 0.55;
                    const isInCart = it.key === "Birria" && tSec >= 0.8;
                    return (
                      <div
                        key={it.key}
                        className="rounded-xl p-3 transition-all border"
                        style={{
                          background: isHighlighted
                            ? "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)"
                            : isInCart
                              ? "rgba(244,114,43,0.06)"
                              : "white",
                          borderColor: isHighlighted
                            ? "transparent"
                            : isInCart
                              ? "rgba(244,114,43,0.30)"
                              : "rgba(0,0,0,0.05)",
                          transform: isHighlighted ? "scale(1.06)" : "scale(1)",
                          boxShadow: isHighlighted
                            ? "0 12px 30px rgba(244,114,43,0.35)"
                            : "0 1px 2px rgba(0,0,0,0.02)",
                        }}
                      >
                        <div className="text-[20px] mb-1">{it.emoji}</div>
                        <div
                          className="text-[12px] font-black"
                          style={{ color: isHighlighted ? "white" : "#1A1A1A" }}
                        >
                          {t(it.label.en, it.label.es)}
                        </div>
                        <div
                          className="text-[10px] font-bold tabular-nums mt-0.5"
                          style={{ color: isHighlighted ? "rgba(255,255,255,0.85)" : "#6B6762" }}
                        >
                          {it.price}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Cart */}
                <div className="col-span-3 bg-[#FAFAF8] border-l border-black/5 p-3 flex flex-col">
                  <div className="text-[9px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
                    {t("Current order", "Orden actual")}
                  </div>

                  {tSec < 0.8 ? (
                    <div className="flex-1 flex items-center justify-center text-[11px] text-redy-secondary/50 font-medium">
                      {t("Tap to add", "Toca para agregar")}
                    </div>
                  ) : (
                    <div className="flex-1 space-y-2">
                      <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.25 }}
                        className="bg-white rounded-lg p-2 border border-black/5"
                      >
                        <div className="flex items-baseline justify-between">
                          <div className="text-[11px] font-black text-redy-charcoal">
                            {t("Birria taco", "Taco de Birria")}
                          </div>
                          <div className="text-[10px] font-bold tabular-nums text-redy-charcoal">
                            $9.00
                          </div>
                        </div>
                        <div className="text-[9px] font-medium" style={{ color: "#D7263D" }}>
                          + {t("Extra cheese", "Extra queso")}
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Bottom: pay button */}
                  <div className="pt-3 border-t border-black/5 mt-3">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] font-bold text-redy-secondary">{t("Total", "Total")}</div>
                      <div className="text-[14px] font-black tabular-nums text-redy-charcoal">
                        {tSec >= 0.8 ? "$9.00" : "$0.00"}
                      </div>
                    </div>
                    <div
                      className="rounded-lg py-2.5 text-center text-[11px] font-black tracking-wide uppercase transition-all"
                      style={{
                        background:
                          tSec >= 1.0 && tSec < 1.2
                            ? "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)"
                            : tSec >= 1.2
                              ? "#2ECC71"
                              : "rgba(244,114,43,0.10)",
                        color:
                          tSec >= 1.0
                            ? "white"
                            : "#F4722B",
                        transform: tSec >= 1.0 && tSec < 1.2 ? "scale(1.03)" : "scale(1)",
                      }}
                    >
                      {tSec >= 1.4 ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Check className="w-3 h-3" strokeWidth={3.5} />
                          {t("Paid · Cash", "Cobrado · Cash")}
                        </span>
                      ) : tSec >= 1.2 ? (
                        t("Cash · $9.00", "Cash · $9.00")
                      ) : tSec >= 1.0 ? (
                        t("Pay", "Cobrar")
                      ) : (
                        t("Pay", "Cobrar")
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Caption row — timer + label */}
              <div className="flex items-center justify-between px-5 py-3 bg-redy-charcoal text-white">
                <div className="flex items-center gap-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-white/60">
                    {t("Step", "Paso")}
                  </div>
                  <div
                    className="text-[13px] font-black"
                    style={{ color: isDone ? "#2ECC71" : "#F4722B" }}
                  >
                    {t(activeStep.label.en, activeStep.label.es)}
                  </div>
                </div>
                <div className="flex items-baseline gap-2 font-mono tabular-nums">
                  <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">
                    {t("Elapsed", "Transcurrido")}
                  </span>
                  <span
                    className="text-[22px] font-black"
                    style={{ color: isDone ? "#2ECC71" : "#F4722B" }}
                  >
                    {tSec.toFixed(2)}
                  </span>
                  <span className="text-[11px] font-bold text-white/60">s</span>
                </div>
              </div>
            </div>
          </div>

          {/* Below mockup: stat killer + CTA */}
          <div className="bg-gradient-to-br from-[#F4722B] to-[#D7263D] rounded-[24px] px-6 py-6 md:px-8 md:py-8 text-white shadow-lg shadow-[#F4722B]/20 mt-6">
            <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
              <div>
                <div className="text-[11px] font-black tracking-widest uppercase text-white/70 mb-2">
                  {t("Order taken in", "Orden tomada en")}
                </div>
                <div className="text-[48px] md:text-[64px] font-black leading-none tabular-nums">
                  1.4s
                </div>
                <div className="text-[13px] font-bold text-white/85 mt-1">
                  {t(
                    "Two taps from menu to fired.",
                    "Dos taps del menú a la cocina."
                  )}
                </div>
              </div>
              <Link
                href="/signup"
                className="inline-flex items-center gap-2 bg-white text-redy-charcoal font-bold text-[14px] px-6 py-3.5 rounded-full hover:scale-[1.03] transition-transform shadow-lg whitespace-nowrap"
              >
                {t("Try the demo POS", "Prueba el POS demo")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8 text-[12px] font-medium text-redy-secondary/60"
        >
          {t(
            "Scripted demo on loop. Real timing depends on your menu and your team.",
            "Demo guionado en loop. El tiempo real depende de tu menú y tu equipo."
          )}
        </motion.div>
      </div>
    </section>
  );
}
